import React from "react";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

const PricingCard = ({
  title,
  price,
  description,
  features,
  isPrimary,
  onClick,
}) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:transform hover:scale-105 flex flex-col h-full ${
        isPrimary ? "border-2 border-coralRed" : ""
      }`}
    >
      <div className="p-8 border-b">
        <h3 className="text-2xl font-bold mb-2 font-sans">{title}</h3>
        <div className="text-4xl font-bold mb-2 font-sans">
          {price}
          <span className="text-lg text-ashGray">
            {price !== "Custom" && price !== "Free"
              ? title === "Pay-Per-Interview"
                ? "/interview"
                : "/month"
              : ""}
          </span>
        </div>
        <p className="text-ashGray font-sans">{description}</p>
      </div>
      <div className="p-8 flex flex-col flex-grow">
        <ul className="space-y-4 mb-8 font-sans flex-grow">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg
                className="w-6 h-6 text-peachYellow mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={onClick}
          data-testid={`pricing-${title.toLowerCase()}-button`}
          className={`w-full py-3 px-4 rounded-lg font-bold transition-all duration-300 font-sans mt-auto ${
            isPrimary
              ? "bg-coralRed text-white hover:bg-coralRed/90"
              : "bg-peachYellow text-black hover:bg-peachYellow/90"
          }`}
        >
          Select Plan
        </button>
      </div>
    </div>
  );
};

const PricingSection = ({
  ref6,
  controls6,
  setShowContactForm,
  setInitialMessage,
}) => {
  const handlePlanSelection = (planTitle) => {
    let message = "";

    switch (planTitle) {
      case "Trial":
        message = "I'd like to start a free trial.";
        break;
      case "Pay-Per-Interview":
        message = "I'm interested in the Pay-Per-Interview plan.";
        break;
      case "Growth":
        message = "I'm interested in the Growth plan.";
        break;
      case "Enterprise":
        message = "I'd like to learn more about your enterprise options.";
        break;
      default:
        message = "";
    }

    setInitialMessage(message);
    setShowContactForm(true);
  };

  return (
    <div className="pb-32 bg-white" ref={ref6}>
      <div className="container mx-auto px-4 mt-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={controls6}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-sans">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-ashGray max-w-3xl mx-auto font-sans">
            {`Choose the plan that works best for your organization's needs.`}
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-4 md:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 50 }}
          animate={controls6}
          transition={{ delay: 0.2 }}
        >
          <PricingCard
            title="Free Trial"
            price="0$"
            description="Try our platform with no commitment"
            features={[
              "No credit card required",
              "Up to 10 interviews",
              "1 month of access",
              "Email support",
              "7-day data retention",
            ]}
            isPrimary={false}
            onClick={() => handlePlanSelection("Trial")}
          />

          <PricingCard
            title="Pay-Per-Interview"
            price="$3"
            description="Perfect for teams with variable hiring needs"
            features={[
              "Pay only for what you use",
              "No monthly commitment",
              "Email & chat support",
              "30-day data retention",
              "Basic customization",
            ]}
            isPrimary={false}
            onClick={() => handlePlanSelection("Pay-Per-Interview")}
          />
          <PricingCard
            title="Growth"
            price="$19"
            description="Grow your team in comfort"
            features={[
              "20 interviews per month",
              "Rollover credit for slow months",
              "Email & chat support",
              "90-day data retention",
              "Full customization options",
            ]}
            isPrimary={true}
            onClick={() => handlePlanSelection("Growth")}
          />

          <PricingCard
            title="Enterprise"
            price="Custom"
            description="Tailored for your organization's specific needs"
            features={[
              "Fixed pricing",
              "Monthly or annual",
              "Priority support",
              "As many interviews as needed",
              "Custom data retention",
              "Full customization options",
            ]}
            isPrimary={false}
            onClick={() => handlePlanSelection("Enterprise")}
          />
        </motion.div>
      </div>
    </div>
  );
};

PricingCard.propTypes = {
  title: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.array.isRequired,
  isPrimary: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

PricingSection.propTypes = {
  ref6: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  controls6: PropTypes.object.isRequired,
  setShowContactForm: PropTypes.func.isRequired,
  setInitialMessage: PropTypes.func.isRequired,
};

export default PricingSection;
